import React, { useEffect, useRef, useState } from "react";
import { JumpStarter, SupportingTheCommunity, Streaks } from './MedalsImageMapping';

type UiMedal = {
  key: string;
  title: string;
  unlocked: boolean;
  imageKey: string;
  earnedAt?: string | null;
  description?: string | null;
};

type UiSection = {
  key: "jumpstarter" | "community" | "streak";
  title: string;
  medals: UiMedal[];
  totalUnlocked?: number | null;
};

function getImage(sectionKey: string, code: string) {
  if (sectionKey === "jumpstarter") return JumpStarter[code];
  if (sectionKey === "community") return SupportingTheCommunity[code];
  if (sectionKey === "streak") return Streaks[code];
  return "";
}

function getUpvoteSizeClass(imageKey: string) {
  if (imageKey === "J3") return "ym-upvote-xsmall";
  if (imageKey === "J4") return "ym-upvote-small";
  return "";
}

const AchievementsSectionRow: React.FC<{
  section: UiSection;
  onViewAll: (section: UiSection) => void;
  showViewAll: boolean;
}> = ({ section, onViewAll, showViewAll }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(section.medals.length > 5);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    const onScroll = () => {
      const nearStart = el.scrollLeft <= 5;
      const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
      setShowLeft(!nearStart);
      setShowRight(!nearEnd && section.medals.length > 5);
    };

    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [section.medals.length]);

  const scrollPage = (dir: "left" | "right") => {
    const el = rowRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>(".ym-medal-card");
    const gap = firstCard ? firstCard.offsetWidth + 16 : 176;
    el.scrollBy({ left: dir === "left" ? -gap * 5 : gap * 5, behavior: "smooth" });
  };

  const isCommunity = section.key === "community";
  const total = section.medals.length;
  const count = typeof section.totalUnlocked === "number" ? section.totalUnlocked : section.medals.filter((m) => m.unlocked).length;

  return (
    <section className="ym-section">
      <header className="ym-section-header">
        <div>
          <h2 className="ym-section-title">
            {section.title}

            {section.key === 'community' && (
              <span className="ym-section-guide">
                - Complete saving 3 favorites in each of these categories to earn that medal.
              </span>
            )}

            {section.key === 'jumpstarter' && (
              <span className="ym-section-guide">
                - Get active in the community by upvoting other people's favorites that you have tasted and also love.
              </span>
            )}

            {section.key === 'streak' && (
              <span className="ym-section-guide">
                - Visit Yolomeal at least once a week to find amazing meals and add consecutive week streak awards to
                your medals earned.
              </span>
            )}
          </h2>
          <div className="ym-section-sub">{`${count} of ${total} Unlocked`}</div>
        </div>
        {!isCommunity && showViewAll && (
          <button className="ym-viewall" type="button" onClick={() => onViewAll(section)}>
            View all
          </button>
        )}
      </header>

      {isCommunity ? (
        <div className="ym-community-grid">
          {section.medals.map((m) => (
            <div key={m.key} className="ym-medal-card">
              <div className="ym-medal-wrap" title={m.title} aria-label={m.title}>
                <img
                  src={m.unlocked ? getImage(section.key, m.imageKey) : getImage(section.key, m.imageKey + '_locked')}
                  alt={m.title}
                  className={`ym-medal-img ${getUpvoteSizeClass(m.imageKey)}`}
                />
              </div>
              <div className={`ym-medal-title ${m.unlocked ? '' : 'is-locked'}`}>{m.title}</div>
              {m.unlocked && m.earnedAt && (
                <div className="ym-medal-date">{new Date(m.earnedAt).toLocaleDateString()}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="ym-scroll-wrap">
          {showLeft && (
            <button
              className="ym-scroll-btn ym-scroll-btn--left"
              aria-label="Scroll left"
              onClick={() => scrollPage('left')}
            />
          )}

          <div className="ym-row-scroll" ref={rowRef}>
            {section.medals.map((m) => (
              <div key={m.key} className="ym-medal-card">
                <div className="ym-medal-wrap" title={m.title} aria-label={m.title}>
                  <img
                    src={m.unlocked ? getImage(section.key, m.imageKey) : getImage(section.key, m.imageKey + '_locked')}
                    alt={m.title}
                    className={`ym-medal-img ${getUpvoteSizeClass(m.imageKey)}`}
                  />
                </div>
                <div className={`ym-medal-title ${m.unlocked ? '' : 'is-locked'}`}>
                  {m.title}

                  {section.key === "jumpstarter" && m.imageKey === "J2" && m.description && (
                    <div className="ym-medal-subtext">
                      {m.description}
                    </div>
                  )}

                  {section.key === "jumpstarter" && m.imageKey === "J6" && (
                    <div className="ym-medal-subtext">
                      (Do at least 5 restaurant searches in a week)
                    </div>
                  )}

                  {section.key === "jumpstarter" && m.imageKey === "J7" && (
                    <div className="ym-medal-subtext">
                      (Use the Filter option 20 times from any category)
                    </div>
                  )}

                  {m.unlocked && m.earnedAt && (
                    <div className="ym-medal-date">{new Date(m.earnedAt).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {showRight && (
            <button
              className="ym-scroll-btn ym-scroll-btn--right"
              aria-label="Scroll right"
              onClick={() => scrollPage('right')}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default AchievementsSectionRow;
